import { Handler, useDrag } from "@use-gesture/react";
import classNames from "classnames";
import { action, untracked } from "mobx";
import { observer } from "mobx-react-lite";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useVirtual } from "react-virtual";
import { Position } from "../Types";
import { FeatherIcon } from "../ui/FeatherIcon";
import { Tooltip } from "../ui/Tooltip";
import {
  getParentByClassName,
  isModifierKeyDown,
  modifierKey,
  throttle,
  useEqualRefValue,
  useSyncRef,
} from "../Utils";
import {
  activePaneStates,
  canCreateNewPaneComputed,
  canvasTransformComputed,
  canvasViewport,
} from "./CanvasState";
import { dragValueAtom, handleNewPaneCreate } from "./NewPane";
import {
  convertViewportPositionToCanvasOffset,
  roundPositionToGrid,
  usePaneOutputDimensions,
} from "./SandboxUtils";

const ROW_HEIGHT = 21;

function useRootModifierClassName(
  rootRef: React.MutableRefObject<HTMLElement | null>,
  canCreateNewPane: boolean
) {
  useEffect(() => {
    if (!canCreateNewPane) {
      return;
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === modifierKey) {
        rootRef.current!.classList.add("json-root-modifier");
      }
    }
    function onKeyUp(e: KeyboardEvent) {
      if (e.key === modifierKey) {
        rootRef.current!.classList.remove("json-root-modifier");
      }
    }
    function onBlur() {
      rootRef.current!.classList.remove("json-root-modifier");
    }
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, [canCreateNewPane]);
}

function useDragNewPane<Memo>(
  onFirst: (
    event: PointerEvent | KeyboardEvent | TouchEvent | MouseEvent,
    args: any[]
  ) => false | any,
  updater: (position: Position, memo: Memo) => void
) {
  return useDrag(
    action<Handler<"drag">>(
      ({ args, first, xy, last, cancel, event, memo = {} }) => {
        if (last) {
          if (dragValueAtom.value !== undefined) {
            handleNewPaneCreate();
            dragValueAtom.value = undefined;
          }
        } else {
          if (first) {
            const firstMemo = onFirst(event, args);
            if (firstMemo === false) {
              cancel();
              return;
            }
            memo = firstMemo;
            // prevent DataTableExtractCellButton bubbling to table root
            event.stopPropagation();
            event.preventDefault();
          }
          updater(xy, memo);
        }
        return memo;
      }
    ),
    {
      transform: ([x, y]) =>
        untracked(() =>
          roundPositionToGrid(
            convertViewportPositionToCanvasOffset(
              [x, y],
              canvasTransformComputed.get(),
              canvasViewport.zoom
            ),
            1
          )
        ),
      pointer: {
        touch: true,
      },
    }
  );
}

function DataTableExtractCellButton({
  dataPaneId,
  rowIndex,
  columnKey,
  isPaneValueObjectFormat,
  canCreateNewPane,
}: {
  dataPaneId: string;
  rowIndex: number;
  columnKey: string | number;
  isPaneValueObjectFormat: boolean;
  canCreateNewPane: boolean;
}) {
  const bindDrag = useDragNewPane(
    () => undefined,
    action((position) => {
      dragValueAtom.value = {
        type: "reference",
        fromPaneId: dataPaneId,
        fromPaneOutputIndex: 0,
        position,
        sourceInfo: {
          type: "key-path",
          keyPath: isPaneValueObjectFormat
            ? ["data", rowIndex, columnKey]
            : [rowIndex, columnKey],
        },
      };
    })
  );

  if (!canCreateNewPane) {
    return (
      <Tooltip content={"switch to source\nto create pane"}>
        <button className="text-neutral-400 hover:text-neutral-700 transition-colors">
          <FeatherIcon icon="external-link" size="xs" />
        </button>
      </Tooltip>
    );
  }
  return (
    <button
      {...bindDrag()}
      className="text-neutral-400 hover:text-neutral-700 transition-colors touch-none"
    >
      <FeatherIcon icon="external-link" size="xs" />
    </button>
  );
}

type DataTableSettingsSort = [
  columnKey: string | number,
  direction: "asc" | "desc"
];
type DataTableSettings = {
  sort: DataTableSettingsSort | undefined;
};
function getSortFunction(
  data: Record<string, any>[],
  [columnKey, direction]: DataTableSettingsSort
) {
  if (data[0] !== undefined && typeof data[0][columnKey] === "number") {
    return (aIndex: number, bIndex: number) =>
      direction === "asc"
        ? data[aIndex][columnKey] - data[bIndex][columnKey]
        : data[bIndex][columnKey] - data[aIndex][columnKey];
  }
  const collator = new Intl.Collator();
  return (aIndex: number, bIndex: number) => {
    return direction === "asc"
      ? collator.compare(data[aIndex][columnKey], data[bIndex][columnKey])
      : collator.compare(data[bIndex][columnKey], data[aIndex][columnKey]);
  };
}

function renderCell(
  value: any,
  dataPaneId: string,
  rowIndex: number,
  key: string | number,
  isPaneValueObjectFormat: boolean,
  canCreateNewPane: boolean
) {
  if (["string", "number"].includes(typeof value)) {
    return (
      <span className="inline-block json-token whitespace-nowrap max-w-full overflow-hidden text-ellipsis">
        {value}
      </span>
    );
  }
  if (typeof value === "boolean") {
    return <span className="json-token">{value ? "true" : "false"}</span>;
  }
  if (value === undefined || value === null) {
    return null;
  }
  return (
    <DataTableExtractCellButton
      dataPaneId={dataPaneId}
      rowIndex={rowIndex}
      columnKey={key}
      isPaneValueObjectFormat={isPaneValueObjectFormat}
      canCreateNewPane={canCreateNewPane}
    />
  );
}

function measureTableColumnWidths(
  schema: DataTableSchema,
  data: any[],
  paneWidth: number
) {
  const tableHtml = `<table style="min-width:${paneWidth}px;"><tbody>
<tr>${schema
    .map(
      (c, i) =>
        `<td class="pr-4${i === 0 ? " pl-2" : ""}">${c.label ?? c.key}</td>`
    )
    .join("")}</tr>
${data
  .map(
    (row) =>
      `<tr>${schema
        .map((c, i) => {
          const value = row[c.key];
          // approximate cell content width
          return `<td class="whitespace-nowrap max-w-[16rem] pr-4 overflow-hidden${
            i === 0 ? " pl-2" : ""
          }">${
            ["string", "number"].includes(typeof value)
              ? value
              : typeof value === "boolean"
              ? "false"
              : "abc"
          }</td>`;
        })
        .join("")}</tr>`
  )
  .join("")}
</tbody></table>`;
  const container = document.createElement("div");
  container.className =
    "absolute top-0 left-0 w-0 h-0 font-mono-natto overflow-hidden";
  container.innerHTML = tableHtml;
  document.body.appendChild(container);
  const headerRow = container.querySelector("tr")!;
  const columnWidths = Array.from(headerRow.querySelectorAll("td")).map(
    (headerCell) => headerCell.offsetWidth
  );
  document.body.removeChild(container);
  return columnWidths;
}

type DataTableSchema = {
  key: string | number;
  label?: string;
}[];
const DataTable = observer(
  ({
    paneId,
    dataPaneId,
    schema: schemaArg,
    data: dataArg,
    onRowClick,
    isPaneValueObjectFormat,
  }: {
    paneId: string;
    dataPaneId: string;
    schema: DataTableSchema | undefined;
    data: (any[] | Object)[];
    onRowClick: Function | undefined;
    isPaneValueObjectFormat: boolean;
  }) => {
    const [settings, setSettings] = useState<DataTableSettings>({
      sort: undefined,
    });
    const [data, autoSchema] = useMemo(() => {
      const filteredDataArg = dataArg.filter((row) => row != null);
      if (filteredDataArg[0] !== undefined) {
        const isArray = Array.isArray(filteredDataArg[0]);
        return [
          filteredDataArg,
          Object.keys(filteredDataArg[0]).map((key, i) => ({
            key: isArray ? i : key,
            label: isArray ? `Column ${i + 1}` : undefined,
          })),
        ];
      }
      return [filteredDataArg, []];
    }, [dataArg]);
    const schema: DataTableSchema = useEqualRefValue(schemaArg ?? autoSchema);
    const sortedIndices = useMemo(() => {
      const indices = new Array(data.length).fill(undefined).map((_, i) => i);
      if (settings.sort === undefined) {
        return indices;
      }
      return indices.sort(getSortFunction(data, settings.sort));
    }, [data, settings]);
    const columnBindDrag = useDrag(
      action<Handler<"drag">>(
        ({ args, first, xy, last, tap, event, memo = {} }) => {
          if (tap) {
            const key = schema[args[0]].key;
            if (settings.sort !== undefined) {
              if (settings.sort[0] !== key) {
                setSettings({
                  sort: [key, "asc"],
                });
              } else if (settings.sort[1] === "asc") {
                setSettings({
                  sort: [key, "desc"],
                });
              } else {
                setSettings({
                  sort: undefined,
                });
              }
            } else {
              setSettings({
                sort: [key, "asc"],
              });
            }
            return;
          }
          if (last) {
            if (dragValueAtom.value !== undefined) {
              handleNewPaneCreate();
              dragValueAtom.value = undefined;
            }
          } else {
            if (first) {
              memo.columnIndex = args[0];
            }
            dragValueAtom.value = {
              type: "reference",
              fromPaneId: dataPaneId,
              fromPaneOutputIndex: 0,
              position: xy,
              sourceInfo: {
                type: "data-column",
                keyPath: [-1, schema[memo.columnIndex].key],
                columnName:
                  schema[memo.columnIndex].label ??
                  `${schema[memo.columnIndex].key}`,
                isPaneValueObjectFormat,
              },
            };
            event?.preventDefault();
          }
          return memo;
        }
      ),
      {
        transform: ([x, y]) =>
          untracked(() =>
            roundPositionToGrid(
              convertViewportPositionToCanvasOffset(
                [x, y],
                canvasTransformComputed.get(),
                canvasViewport.zoom
              ),
              1
            )
          ),
        filterTaps: true,
      }
    );

    const rootRef = useRef<HTMLDivElement | null>(null);
    const canCreateNewPane = canCreateNewPaneComputed.get();
    useRootModifierClassName(rootRef, canCreateNewPane);
    const dataBindDrag = useDragNewPane<{ keyPath: (number | string)[] }>(
      (event) => {
        if (!canCreateNewPane) {
          return false;
        }
        if (
          !(
            isModifierKeyDown(event.metaKey, event.ctrlKey) ||
            (event as any).touches?.length === 2
          ) ||
          !(event.target instanceof HTMLElement)
        ) {
          return false;
        }
        const valueNode = getParentByClassName(event.target, "json-token");
        if (valueNode === undefined) {
          return false;
        }
        let node: Element | null = valueNode;
        const keyPath: string[] = [];
        while (node !== null && node.getAttribute("data-render") !== "root") {
          const nodeKey = node.getAttribute("data-key");
          if (nodeKey !== null) {
            keyPath.unshift(JSON.parse(nodeKey));
          }
          node = node.parentElement;
        }
        if (isPaneValueObjectFormat) {
          keyPath.unshift("data");
        }
        return { keyPath };
      },
      (position, memo) => {
        dragValueAtom.value = {
          type: "reference",
          fromPaneId: dataPaneId,
          fromPaneOutputIndex: 0,
          position,
          sourceInfo: {
            type: "key-path",
            keyPath: memo.keyPath,
          },
        };
      }
    );

    const activePaneState = activePaneStates.get(paneId)!;
    const rowVirtualizer = useVirtual({
      size: sortedIndices.length,
      parentRef: rootRef,
      estimateSize: useCallback(() => ROW_HEIGHT, []),
      paddingEnd: 4,
      useObserver: useCallback(
        () => usePaneOutputDimensions(activePaneState),
        [activePaneState]
      ),
    });
    const [columnWidths, setColumnWidths] = useState<number[] | undefined>(
      undefined
    );
    const dataRef = useSyncRef(data);
    const schemaRef = useSyncRef(schema);
    const columnWidthsRef = useSyncRef(columnWidths);
    const updateColumnWidths = useCallback(() => {
      updateColumnWidthsThrottled.cancel();
      if (rootRef.current !== null) {
        const rootWidth = rootRef.current.offsetWidth;
        const colomnWidths = measureTableColumnWidths(
          schemaRef.current,
          // measure just the first 32 rows
          dataRef.current.slice(0, 32),
          rootWidth
        );
        setColumnWidths(colomnWidths);
      }
    }, []);
    const [updateColumnWidthsThrottled] = useState(() =>
      throttle(() => {
        updateColumnWidths();
      }, 50)
    );
    useEffect(() => () => updateColumnWidthsThrottled.cancel(), []);
    useEffect(() => {
      updateColumnWidths();
    }, [schema]);
    useEffect(() => {
      if (columnWidthsRef.current !== undefined) {
        updateColumnWidthsThrottled();
      }
    }, [data]);
    useEffect(() => {
      const ro = new ResizeObserver((entries) => {
        if (columnWidthsRef.current !== undefined) {
          updateColumnWidthsThrottled();
        }
      });
      const rootElement = rootRef.current!;
      ro.observe(rootElement);
      return () => {
        ro.unobserve(rootElement);
      };
    }, []);

    return (
      <div
        {...dataBindDrag()}
        data-render="root"
        className="h-full w-full overflow-auto font-mono-natto relative"
        ref={rootRef}
      >
        {schema.length !== 0 && columnWidths !== undefined ? (
          <>
            <div
              className="sticky top-0 flex h-7 bg-neutral-0 z-1"
              style={{ width: `${columnWidths.reduce((a, b) => a + b, 0)}px` }}
            >
              {schema.map(({ key, label: name }, i) => (
                <div
                  className={classNames(
                    "py-1 pr-2 whitespace-nowrap text-left font-semibold bg-neutral-0 rounded-sm touch-none",
                    i === 0 ? "pl-2" : undefined
                  )}
                  style={{ width: `${columnWidths[i]}px` }}
                  {...(canCreateNewPane ? columnBindDrag(i) : {})}
                  key={i}
                >
                  <Tooltip
                    content={
                      <div>
                        <div>click to sort</div>
                        {canCreateNewPane ? <div>drag to extract</div> : null}
                      </div>
                    }
                    delayDuration={500}
                  >
                    <div className="-mx-0.5 px-0.5 py-0.5 cursor-pointer hover:bg-primary-100 rounded-sm flex items-center">
                      {name ?? key}
                      {settings.sort !== undefined &&
                      settings.sort[0] === key ? (
                        <FeatherIcon
                          icon={
                            settings.sort[1] === "asc"
                              ? "arrow-down"
                              : "arrow-up"
                          }
                          size="sm"
                        />
                      ) : null}
                    </div>
                  </Tooltip>
                </div>
              ))}
            </div>
            <div
              className="relative"
              style={{ height: `${rowVirtualizer.totalSize}px` }}
            >
              {rowVirtualizer.virtualItems.map((virtualRow) => {
                const rowIndex = sortedIndices[virtualRow.index];
                return (
                  <div
                    onClick={() => {
                      onRowClick?.(data[rowIndex]);
                    }}
                    className="absolute top-0 left-0 flex border-b border-neutral-100 hover:bg-neutral-50"
                    style={{
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                    data-key={`${rowIndex}`}
                    key={virtualRow.index}
                  >
                    {schema.map(({ key }, i) => {
                      // @ts-ignore
                      const value = data[rowIndex][key];
                      return (
                        <div
                          className={classNames(
                            "py-0.5",
                            i === 0 ? "pl-2" : undefined
                          )}
                          style={{ width: `${columnWidths[i]}px` }}
                          data-key={`"${key}"`}
                          key={key}
                        >
                          {renderCell(
                            value,
                            dataPaneId,
                            rowIndex,
                            key,
                            isPaneValueObjectFormat,
                            canCreateNewPane
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </>
        ) : null}
      </div>
    );
  }
);

const SimpleDataTable = observer(
  ({
    paneId,
    dataPaneId,
    data,
    onRowClick,
    isPaneValueObjectFormat,
  }: {
    paneId: string;
    dataPaneId: string;
    data: (any[] | Object)[];
    onRowClick: Function | undefined;
    isPaneValueObjectFormat: boolean;
  }) => {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const canCreateNewPane = canCreateNewPaneComputed.get();
    const dataBindDrag = useDragNewPane<{ keyPath: (number | string)[] }>(
      (event) => {
        if (!canCreateNewPane) {
          return false;
        }
        if (
          !(
            isModifierKeyDown(event.metaKey, event.ctrlKey) ||
            (event as any).touches?.length === 2
          ) ||
          !(event.target instanceof HTMLElement)
        ) {
          return false;
        }
        const valueNode = getParentByClassName(event.target, "json-token");
        if (valueNode === undefined) {
          return false;
        }
        let node: Element | null = valueNode;
        const keyPath: string[] = [];
        while (node !== null && node.getAttribute("data-render") !== "root") {
          const nodeKey = node.getAttribute("data-key");
          if (nodeKey !== null) {
            keyPath.unshift(JSON.parse(nodeKey));
          }
          node = node.parentElement;
        }
        if (isPaneValueObjectFormat) {
          keyPath.unshift("data");
        }
        return { keyPath };
      },
      (position, memo) => {
        dragValueAtom.value = {
          type: "reference",
          fromPaneId: dataPaneId,
          fromPaneOutputIndex: 0,
          position,
          sourceInfo: {
            type: "key-path",
            keyPath: memo.keyPath,
          },
        };
      }
    );
    useRootModifierClassName(rootRef, canCreateNewPane);

    const activePaneState = activePaneStates.get(paneId)!;
    const rowVirtualizer = useVirtual({
      size: data.length,
      parentRef: rootRef,
      estimateSize: useCallback(() => ROW_HEIGHT, []),
      paddingStart: 4,
      paddingEnd: 4,
      useObserver: useCallback(
        () => usePaneOutputDimensions(activePaneState),
        [activePaneState]
      ),
    });

    return (
      <div
        data-render="root"
        {...dataBindDrag()}
        className="h-full w-full overflow-auto font-mono-natto"
        ref={rootRef}
      >
        <div
          className="relative"
          style={{ height: `${rowVirtualizer.totalSize}px` }}
        >
          {rowVirtualizer.virtualItems.map((virtualRow) => {
            const rowIndex = virtualRow.index;
            const datum = data[rowIndex];
            return (
              <div
                onClick={() => {
                  onRowClick?.(datum);
                }}
                className="absolute w-full px-2 py-0.5 border-b border-neutral-100 hover:bg-neutral-50"
                style={{
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
                data-key={`${rowIndex}`}
                key={rowIndex}
              >
                {typeof datum === "boolean" ? (
                  <span className="json-token text-neutral-400">
                    {datum ? "true" : "false"}
                  </span>
                ) : datum === null || datum === undefined ? (
                  <span className="json-token text-neutral-400">
                    {String(datum)}
                  </span>
                ) : (
                  <span className="json-token whitespace-nowrap">
                    {String(datum)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);

export function TableRenderOutput({
  value,
  paneId,
  dataPaneId,
}: {
  value: any;
  paneId: string;
  dataPaneId: string;
}) {
  let data = value;
  let isPaneValueObjectFormat = false;
  try {
    if ("data" in value) {
      data = value.data;
      isPaneValueObjectFormat = true;
    }
  } catch {}
  if (!Array.isArray(data)) {
    return <div className="p-2 text-neutral-400">value is not an array</div>;
  }
  if (data.length === 0) {
    return null;
  }
  if (
    data[0] != null &&
    typeof data[0] !== "string" &&
    (Array.isArray(data[0]) || Object.keys(data[0]).length > 0)
  ) {
    return (
      <DataTable
        paneId={paneId}
        dataPaneId={dataPaneId}
        data={data}
        schema={"schema" in value ? value.schema : undefined}
        onRowClick={"onRowClick" in value ? value.onRowClick : undefined}
        isPaneValueObjectFormat={isPaneValueObjectFormat}
      />
    );
  }
  return (
    <SimpleDataTable
      paneId={paneId}
      dataPaneId={dataPaneId}
      data={data}
      onRowClick={"onRowClick" in value ? value.onRowClick : undefined}
      isPaneValueObjectFormat={isPaneValueObjectFormat}
    />
  );
}
